import { useState } from "react";
import { motion } from "framer-motion";
import {
  MapPin,
  Heart,
  Coffee,
  MessageCircle,
  Mail,
  Star,
  Sparkles,
  Leaf,
  Instagram,
  Quote,
} from "lucide-react";
import DemoBackBar from "../components/DemoBackBar";
import { cafe } from "../data/demos";
import { contact } from "../data/contact";

const featuredOfWeek = {
  name: "Saffron Cardamom Latte",
  story:
    "A limited release this season — Iranian saffron infused into hand-foamed milk, finished with cardamom sugar and a single rose petal.",
  img:
    "https://images.unsplash.com/photo-1572490122747-3968b75cc699?w=1200&q=80",
  price: "₪22",
};

const origins = [
  {
    region: "Yirgacheffe, Ethiopia",
    notes: "Jasmine · bergamot · lemon zest",
    altitude: "1,950m",
    process: "Washed",
  },
  {
    region: "Huila, Colombia",
    notes: "Cocoa · brown sugar · stone fruit",
    altitude: "1,750m",
    process: "Honey",
  },
  {
    region: "Sidamo, Ethiopia",
    notes: "Blueberry · dark chocolate · syrupy",
    altitude: "2,100m",
    process: "Natural",
  },
];

const press = ["Time Out", "Vogue Living", "Monocle", "Eater", "Esquire"];

const reviews = [
  {
    name: "Sara N.",
    quote:
      "Found my third place. The pour-over flight on a Sunday morning is genuinely meditative.",
  },
  {
    name: "Liam J.",
    quote:
      "The pistachio croissant should be illegal. Also, the room is the warmest in the city.",
  },
  {
    name: "Tara M.",
    quote:
      "Service knows their beans. Asked for something fruity — got the most beautiful natural Sidamo I've had.",
  },
];

const formatIls = (value) => `₪${Number(value).toLocaleString("en-IL")}`;

export default function CafeDemo() {
  const wa = `https://wa.me/${contact.whatsapp.replace(/[^0-9]/g, "")}?text=${encodeURIComponent(
    "Hi Velvet Cup, I'd like to place an order."
  )}`;

  return (
    <div className="bg-velvet-bg text-velvet-cream min-h-screen overflow-x-hidden">
      <DemoBackBar accentColor="#D8B4E2" demoName="Velvet Cup · Café" />

      <header className="sticky top-[40px] z-40 backdrop-blur-md bg-velvet-bg/80 border-b border-white/5">
        <div className="mx-auto max-w-7xl px-4 sm:px-8 py-3 sm:py-4 flex items-center justify-between gap-3">
          <a href="#top" className="flex items-center gap-2 sm:gap-3 min-w-0">
            <Coffee size={16} className="text-velvet-rose flex-shrink-0" />
            <span className="font-serif-display text-xl sm:text-2xl italic truncate">Velvet Cup</span>
          </a>
          <nav className="hidden lg:flex items-center gap-8 text-[15px] font-semibold text-velvet-cream/75">
            <a href="#featured" className="hover:text-velvet-rose transition-colors">This Week</a>
            <a href="#menu" className="hover:text-velvet-rose transition-colors">Menu</a>
            <a href="#origins" className="hover:text-velvet-rose transition-colors">Beans</a>
            <a href="#offers" className="hover:text-velvet-rose transition-colors">Offers</a>
            <a href="#gallery" className="hover:text-velvet-rose transition-colors">Gallery</a>
            <a href="#loyalty" className="hover:text-velvet-rose transition-colors">Loyalty</a>
          </nav>
          <a
            href={wa}
            target="_blank"
            rel="noreferrer"
            className="text-[10px] sm:text-xs uppercase tracking-[0.18em] sm:tracking-[0.2em] bg-velvet-rose text-velvet-bg font-bold px-4 sm:px-5 py-2.5 sm:py-3 hover:bg-velvet-cream transition-colors flex-shrink-0"
            data-testid="cafe-order-top"
          >
            Order
          </a>
        </div>
      </header>

      {/* Hero */}
      <section
        id="top"
        className="relative grid lg:grid-cols-2 gap-0 min-h-[88vh] lg:min-h-[80vh]"
        data-testid="cafe-hero"
      >
        <div className="relative flex items-center px-5 sm:px-8 py-16 sm:py-20">
          <div>
            <span className="text-[10px] uppercase tracking-[0.4em] text-velvet-rose">
              Specialty café · Est. 2019
            </span>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9 }}
              className="mt-5 sm:mt-6 font-serif-display text-[52px] leading-[0.95] sm:text-7xl md:text-8xl italic font-light"
            >
              Slow coffee.<br />
              <span className="text-velvet-rose">Soft afternoons.</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-5 sm:mt-6 max-w-md text-velvet-cream/75 text-base sm:text-lg"
            >
              {cafe.about}
            </motion.p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <a
                href="#menu"
                className="bg-velvet-rose text-velvet-bg font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 hover:bg-velvet-cream transition-colors text-center"
                data-testid="cafe-cta-menu"
              >
                See the menu
              </a>
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 border border-velvet-cream/30 hover:border-velvet-rose font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 transition-colors"
                data-testid="cafe-cta-order"
              >
                <MessageCircle size={14} /> WhatsApp Order
              </a>
            </div>
            <div className="mt-10 flex items-center gap-5 text-velvet-cream/60 text-xs">
              <div className="flex gap-1 text-velvet-rose">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} size={12} fill="currentColor" />
                ))}
              </div>
              <span>4.9 · 820+ reviews</span>
            </div>
          </div>
        </div>
        <div className="relative min-h-[380px] lg:min-h-full">
          <img
            src={cafe.hero}
            alt={cafe.name}
            fetchPriority="high"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-velvet-bg via-transparent to-transparent lg:bg-gradient-to-l" />
        </div>
      </section>

      {/* Press */}
      <section className="border-y border-white/5 bg-black/30">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-6 sm:py-8 flex flex-col sm:flex-row items-center sm:justify-between gap-5">
          <span className="text-[10px] uppercase tracking-[0.4em] text-velvet-rose">As featured in</span>
          <div className="flex flex-wrap items-center justify-center gap-x-6 sm:gap-x-10 gap-y-3 text-velvet-cream/55 font-serif-display italic text-base sm:text-lg">
            {press.map((p) => (
              <span key={p}>{p}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Featured this week */}
      <section
        id="featured"
        className="px-5 sm:px-8 py-20 md:py-28"
        data-testid="cafe-featured"
      >
        <div className="mx-auto max-w-7xl grid gap-10 lg:grid-cols-2 items-center">
          <div className="relative aspect-[4/5] overflow-hidden rounded-sm order-2 lg:order-1">
            <img
              src={featuredOfWeek.img}
              alt={featuredOfWeek.name}
              loading="lazy"
              decoding="async"
              className="absolute inset-0 h-full w-full object-cover hover:scale-105 transition-transform duration-[1500ms]"
            />
            <span className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-velvet-rose text-velvet-bg text-[10px] uppercase tracking-[0.25em] font-bold">
              <Sparkles size={11} className="inline -mt-0.5 mr-1" />
              This week
            </span>
          </div>
          <div className="order-1 lg:order-2">
            <div className="text-[10px] uppercase tracking-[0.4em] text-velvet-rose">Drink of the week</div>
            <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl italic font-light leading-[1.05]">
              {featuredOfWeek.name}
            </h2>
            <p className="mt-5 text-velvet-cream/75 leading-relaxed text-base sm:text-lg max-w-md">
              {featuredOfWeek.story}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4">
              <span className="font-display text-3xl font-bold text-velvet-rose">{featuredOfWeek.price}</span>
              <a
                href={wa}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-velvet-rose text-velvet-bg font-bold uppercase tracking-[0.2em] text-xs px-7 py-4 hover:bg-velvet-cream transition-colors"
              >
                <MessageCircle size={14} /> Order one
              </a>
            </div>
            <p className="mt-6 text-xs text-velvet-cream/40">Available while it lasts. Until next Sunday.</p>
          </div>
        </div>
      </section>

      {/* Menu */}
      <section id="menu" className="px-5 sm:px-8 py-24 md:py-32 bg-velvet-plum/20" data-testid="cafe-menu-section">
        <div className="mx-auto max-w-7xl grid gap-10 md:gap-12 md:grid-cols-2">
          <MenuColumn title="Coffee" items={cafe.coffee} test="coffee" />
          <MenuColumn title="Sweet Things" items={cafe.desserts} test="dessert" />
        </div>
      </section>

      {/* Bean origins */}
      <section
        id="origins"
        className="px-5 sm:px-8 py-20 md:py-28"
        data-testid="cafe-origins"
      >
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.4em] text-velvet-rose">
            <Leaf size={14} /> Bean origins
          </div>
          <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl italic font-light leading-[1.05]">
            Sourced direct.<br /> Roasted weekly.
          </h2>
          <p className="mt-5 max-w-2xl text-velvet-cream/65 text-base sm:text-lg leading-relaxed">
            We work with three farms we know by name — and rotate single-origins
            on the bar so you always taste something fresh.
          </p>
          <div className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-3">
            {origins.map((o, i) => (
              <motion.div
                key={o.region}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-white/10 rounded-sm p-6 sm:p-7 hover:border-velvet-rose/50 transition-colors bg-black/30"
              >
                <div className="text-[10px] uppercase tracking-[0.3em] text-velvet-rose">
                  Origin 0{i + 1}
                </div>
                <h3 className="mt-3 font-serif-display text-2xl italic">{o.region}</h3>
                <p className="mt-3 text-sm text-velvet-cream/70 leading-relaxed">
                  Notes — <span className="text-velvet-cream/90">{o.notes}</span>
                </p>
                <div className="mt-5 flex justify-between text-xs text-velvet-cream/55 font-mono-tech border-t border-white/10 pt-4">
                  <span>Alt {o.altitude}</span>
                  <span>{o.process}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Offers */}
      <section
        id="offers"
        className="px-5 sm:px-8 py-20 md:py-28 bg-velvet-plum/30 border-y border-velvet-rose/15"
        data-testid="cafe-offers"
      >
        <div className="mx-auto max-w-7xl">
          <div className="text-[10px] uppercase tracking-[0.4em] text-velvet-rose">Specials</div>
          <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl italic font-light leading-[1.05]">
            Little excuses to come in.
          </h2>
          <div className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-3">
            {cafe.offers.map((o, i) => (
              <motion.div
                key={o.title}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="bg-velvet-bg/60 border border-velvet-rose/20 rounded-xl p-6 sm:p-7 hover:border-velvet-rose transition-colors"
                data-testid={`offer-${i}`}
              >
                <h3 className="font-display text-xl sm:text-2xl font-bold">{o.title}</h3>
                <p className="mt-2 text-sm text-velvet-cream/70 leading-relaxed">{o.body}</p>
                {o.price && (
                  <span className="mt-5 inline-block font-mono-tech text-velvet-rose text-sm">
                    {o.price}
                  </span>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section id="gallery" className="px-5 sm:px-8 py-20 md:py-28" data-testid="cafe-gallery">
        <div className="mx-auto max-w-7xl">
          <div className="text-[10px] uppercase tracking-[0.4em] text-velvet-rose">Gallery</div>
          <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl italic font-light leading-[1.05]">
            A room for staying.
          </h2>
          <div className="mt-10 sm:mt-12 grid gap-3 grid-cols-2 md:grid-cols-3">
            {cafe.gallery.map((src, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, scale: 0.96 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.05 }}
                className={`overflow-hidden ${i % 5 === 0 ? "aspect-square md:aspect-[4/5]" : "aspect-square"}`}
              >
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="h-full w-full object-cover hover:scale-110 transition-transform duration-[1500ms]"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="px-5 sm:px-8 py-20 md:py-28 bg-velvet-plum/20">
        <div className="mx-auto max-w-7xl">
          <div className="text-[10px] uppercase tracking-[0.4em] text-velvet-rose">Regulars say</div>
          <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl italic font-light leading-[1.05]">
            Quiet, in the best way.
          </h2>
          <div className="mt-12 grid gap-5 sm:gap-6 md:grid-cols-3">
            {reviews.map((r, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-velvet-rose/20 p-6 sm:p-7 rounded-sm bg-velvet-bg/40"
              >
                <Quote size={22} className="text-velvet-rose/60" />
                <p className="mt-4 text-velvet-cream/85 leading-relaxed">"{r.quote}"</p>
                <p className="mt-5 text-xs uppercase tracking-[0.25em] text-velvet-cream/45">— {r.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Loyalty */}
      <section
        id="loyalty"
        className="px-5 sm:px-8 py-20 md:py-28 bg-gradient-to-b from-velvet-bg to-black"
        data-testid="cafe-loyalty"
      >
        <div className="mx-auto max-w-4xl text-center">
          <Heart size={26} className="text-velvet-rose mx-auto" />
          <h2 className="mt-5 font-serif-display text-4xl sm:text-5xl md:text-6xl italic font-light">
            Velvet Loyalty.
          </h2>
          <p className="mt-5 text-velvet-cream/70 max-w-2xl mx-auto text-base sm:text-lg">
            No app, no plastic card. Tell us your name on your first visit and
            every 9th drink is on us. Forever.
          </p>
          <div className="mt-10 grid grid-cols-9 gap-1.5 sm:gap-2 max-w-md mx-auto">
            {Array.from({ length: 9 }).map((_, i) => (
              <div
                key={i}
                className={`aspect-square rounded-full border-2 ${
                  i < 5 ? "bg-velvet-rose border-velvet-rose" : "border-velvet-rose/40"
                }`}
              />
            ))}
          </div>
          <p className="mt-6 text-xs text-velvet-cream/50">5 of 9 — example card.</p>
        </div>
      </section>

      {/* Newsletter */}
      <section className="px-5 sm:px-8 py-20 bg-velvet-bg border-t border-white/5">
        <div className="mx-auto max-w-3xl text-center">
          <Mail size={22} className="text-velvet-rose mx-auto" />
          <h3 className="mt-4 font-serif-display text-3xl sm:text-4xl italic font-light">
            Slow newsletter.
          </h3>
          <p className="mt-3 text-velvet-cream/65 max-w-md mx-auto text-sm sm:text-base">
            New beans, new pastries, listening sessions. One email a month. Soft.
          </p>
          <form
            onSubmit={(e) => e.preventDefault()}
            className="mt-7 flex flex-col sm:flex-row gap-3 max-w-md mx-auto"
          >
            <input
              type="email"
              required
              placeholder="you@example.com"
              className="flex-1 bg-white/5 border border-white/10 px-5 py-3 text-sm focus:border-velvet-rose focus:outline-none rounded-sm"
            />
            <button
              type="submit"
              className="bg-velvet-rose text-velvet-bg text-xs uppercase tracking-[0.2em] font-bold px-6 py-3 hover:bg-velvet-cream transition-colors rounded-sm"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      {/* Visit */}
      <section className="px-5 sm:px-8 py-20 md:py-24 bg-velvet-plum/20" data-testid="cafe-visit">
        <div className="mx-auto max-w-7xl grid gap-10 md:grid-cols-2">
          <div>
            <div className="text-[10px] uppercase tracking-[0.4em] text-velvet-rose">Visit</div>
            <h2 className="mt-3 font-serif-display text-4xl sm:text-5xl italic font-light">Come over.</h2>
            <ul className="mt-8 space-y-4 text-velvet-cream/85">
              <li className="flex items-center gap-3 text-sm sm:text-base">
                <MapPin size={16} className="text-velvet-rose flex-shrink-0" /> {cafe.address}
              </li>
              <li className="flex items-center gap-3 text-sm sm:text-base">
                <Instagram size={16} className="text-velvet-rose flex-shrink-0" /> @velvetcup
              </li>
            </ul>
            <h3 className="mt-10 text-[10px] uppercase tracking-[0.4em] text-velvet-rose">Hours</h3>
            <ul className="mt-4 space-y-2 text-velvet-cream/75 font-mono-tech text-xs sm:text-sm">
              {cafe.hours.map((h) => (
                <li key={h.day} className="flex justify-between max-w-xs border-b border-white/5 py-2">
                  <span>{h.day}</span><span>{h.time}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl overflow-hidden border border-white/10 aspect-[4/3] md:aspect-auto md:min-h-[360px]">
            <img
              src="https://images.unsplash.com/photo-1559925393-8be0ec4767c8?w=1200&q=80"
              alt="Cafe interior"
              loading="lazy"
              decoding="async"
              className="h-full w-full object-cover hover:scale-105 transition-transform duration-[1500ms]"
            />
          </div>
        </div>
      </section>

      <footer className="border-t border-white/5 px-5 sm:px-8 py-10 text-center text-xs text-velvet-cream/40">
        © {new Date().getFullYear()} Velvet Cup Café — Demo by Salama Solutions
      </footer>
    </div>
  );
}

function MenuColumn({ title, items, test }) {
  return (
    <div data-testid={`cafe-menu-${test}`}>
      <div className="text-[10px] uppercase tracking-[0.4em] text-velvet-rose">{title}</div>
      <h3 className="mt-3 font-serif-display text-4xl sm:text-5xl md:text-6xl italic font-light mb-6 sm:mb-8">
        {title === "Coffee" ? "On the bar." : "From the case."}
      </h3>
      <ul className="divide-y divide-white/5">
        {items.map((it) => (
          <li key={it.name} className="flex items-start justify-between py-5 sm:py-6 gap-4 group">
            <div className="min-w-0">
              <div className="font-display text-lg sm:text-xl font-bold leading-tight">{it.name}</div>
              <div className="mt-1 text-sm sm:text-base text-velvet-cream/55 leading-relaxed">{it.desc}</div>
            </div>
            <div className="font-mono-tech text-velvet-rose text-base sm:text-lg flex-shrink-0">{formatIls(it.price)}</div>
          </li>
        ))}
      </ul>
    </div>
  );
}
